import * as React from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";
import { Button } from "./button";
import { Input } from "./input";
import { cn } from "../../utils/utils.js";
import { RANK_LABELS, isListedRank } from "../../utils/constants";

const RANK_OTHER = "__OTHER__";

export type RankSelectProps = {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Show an Other option that lets the user type a free-text rank. */
  allowOther?: boolean;
  /** Ranks already chosen (e.g. multi-select chips). Hidden from the list. */
  exclude?: string[];
  /** Optional aria / error styling on the trigger. */
  invalid?: boolean;
};

export function RankSelect({
  value = "",
  onValueChange,
  placeholder = "Search and select rank…",
  disabled = false,
  id,
  className,
  triggerClassName,
  contentClassName,
  allowOther = false,
  exclude = [],
  invalid = false,
}: RankSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [otherMode, setOtherMode] = React.useState(
    () => allowOther && !!value && !isListedRank(value),
  );
  const searchRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!allowOther) return;
    setOtherMode(!!value && !isListedRank(value));
  }, [value, allowOther]);

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const excludeSet = React.useMemo(
    () => new Set(exclude.map((r) => r.toLowerCase())),
    [exclude],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return RANK_LABELS.filter((label) => {
      if (excludeSet.has(label.toLowerCase())) return false;
      if (!q) return true;
      return label.toLowerCase().includes(q);
    });
  }, [query, excludeSet]);

  const displayValue = otherMode
    ? value
      ? `Other: ${value}`
      : "Other…"
    : value || "";

  const selectRank = (label: string) => {
    setOtherMode(false);
    onValueChange(label);
    setOpen(false);
  };

  const selectOther = () => {
    setOtherMode(true);
    if (isListedRank(value)) onValueChange("");
    setOpen(false);
  };

  return (
    <div className={cn("w-full space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={invalid || undefined}
            disabled={disabled}
            className={cn(
              "h-auto min-h-10 w-full justify-between gap-2 px-3 py-2 text-left font-normal whitespace-normal",
              !displayValue && "text-muted-foreground",
              invalid && "!border-red-600",
              triggerClassName,
            )}
          >
            <span className="line-clamp-2 flex-1 text-sm leading-snug break-words">
              {displayValue || placeholder}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className={cn(
            "z-[80] w-[var(--radix-popover-trigger-width)] min-w-[18rem] max-w-[min(100vw-2rem,36rem)] p-0",
            contentClassName,
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search ranks…"
                className="h-10 pr-8 pl-9"
                aria-label="Search ranks"
              />
              {query ? (
                <button
                  type="button"
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                >
                  <X className="size-4" />
                </button>
              ) : null}
            </div>
            <p className="mt-1.5 px-0.5 text-[11px] text-slate-500">
              {filtered.length} of {RANK_LABELS.length} ranks
              {query.trim() ? " match" : ""}
            </p>
          </div>

          <ScrollArea className="h-[min(50vh,320px)]">
            <ul className="p-1" role="listbox">
              {filtered.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-slate-500">
                  No ranks match “{query.trim()}”
                </li>
              ) : (
                filtered.map((label) => {
                  const selected =
                    !otherMode &&
                    value.trim().toLowerCase() === label.toLowerCase();
                  return (
                    <li key={label}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={cn(
                          "flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left text-sm leading-snug break-words transition-colors hover:bg-[var(--primary-font)] hover:text-white",
                          selected && "bg-[var(--primary-font)]/10 font-medium",
                        )}
                        onClick={() => selectRank(label)}
                      >
                        <Check
                          className={cn(
                            "mt-0.5 size-4 shrink-0",
                            selected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="min-w-0 flex-1">{label}</span>
                      </button>
                    </li>
                  );
                })
              )}
              {allowOther ? (
                <li className="sticky bottom-0 border-t bg-white">
                  <button
                    type="button"
                    role="option"
                    aria-selected={otherMode}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-[var(--primary-font)] hover:text-white",
                      otherMode && "bg-[var(--primary-font)]/10",
                    )}
                    onClick={selectOther}
                  >
                    <Check
                      className={cn(
                        "mt-0.5 size-4 shrink-0",
                        otherMode ? "opacity-100" : "opacity-0",
                      )}
                    />
                    Other…
                  </button>
                </li>
              ) : null}
            </ul>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {allowOther && otherMode ? (
        <Input
          aria-label="Rank / Designation (other)"
          placeholder="Type the rank"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}

export { RANK_OTHER };
